// Formatting helpers for the invoice-payment UI (CHO-754) and the shared
// pieces the queue, the single-invoice flow, and the pay-all run reuse
// (CHO-762).
import type { PaymentAttemptStatus, PayableInvoice } from '../types/payments';

/**
 * The queue payload arrives either as a bare array or wrapped as
 * `{ invoices }` — the page, and the pay-all snapshot, accept both.
 */
export function extractInvoices(payload: unknown): PayableInvoice[] {
  if (Array.isArray(payload)) return payload as PayableInvoice[];
  if (payload && typeof payload === 'object' && Array.isArray((payload as { invoices?: unknown }).invoices)) {
    return (payload as { invoices: PayableInvoice[] }).invoices;
  }
  return [];
}

/** Queue ordering key (CHO-754): missing due dates sink to the bottom. */
export function dueDateSortKey(invoice: Pick<PayableInvoice, 'dueDate'>): string {
  return invoice.dueDate ?? '9999-12-31';
}

/** Last four digits only, always masked. The service never sends more. */
export function formatCardRef(last4: string | null | undefined): string {
  if (!last4) return '—';
  return `•••• ${last4}`;
}

/** 7_000 -> "0:07", 65_000 -> "1:05", 3_723_000 -> "1:02:03". */
export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${minutes}:${ss}`;
}

/** '2026-09-20' or ISO -> '20 Sep 2026'; '—' when missing/unparseable. */
export function formatInvoiceDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const ATTEMPT_STATUS_LABELS: Record<PaymentAttemptStatus, string> = {
  initiated: 'Awaiting approval',
  succeeded: 'Succeeded',
  failed: 'Failed',
  unknown: 'Unknown — blocked',
  dry_run: 'Dry run — no payment',
};

export const ATTEMPT_STATUS_CLASS: Record<PaymentAttemptStatus, string> = {
  initiated: 'bg-sky-50 text-sky-700 border-sky-200',
  succeeded: 'bg-green-50 text-green-700 border-green-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  unknown: 'bg-amber-50 text-amber-800 border-amber-300',
  dry_run: 'bg-gray-50 text-gray-600 border-gray-200',
};

/** Property column fallback — the service guarantees propertyId, propertyName is optional. */
export function formatPropertyLabel(invoice: Pick<PayableInvoice, 'propertyId' | 'propertyName'>): string {
  return invoice.propertyName ?? `Property ${invoice.propertyId}`;
}
